import { useState, useMemo } from 'react';

import get from 'lodash/get';
import orderBy from 'lodash/orderBy';

interface UseTableFilterOptions {
  // Columns are used to drive type-aware sorting. Any column declared as
  // `type: 'number'` is sorted numerically (1, 2, 10) instead of as a string
  // (1, 10, 2), regardless of the values currently present.
  columns?: Array<{ id: string; type?: string }>;
}

const isPopulated = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

const createSortIteratee = (columnId: string | number, numericColumnIds: Set<string>) => {
  if (!numericColumnIds.has(columnId.toString())) {
    return columnId;
  }

  return (row: unknown) => {
    const value = get(row, columnId);
    return isPopulated(value) ? Number(value) : Number.NEGATIVE_INFINITY;
  };
};

const checkForFilters = (row: unknown, filter: PaginationFilter) => {
  switch (filter.condition) {
    case 'contains':
      return (
        (get(row, filter.columnId) || '')
          .toLowerCase()
          .indexOf(filter.value.toString().toLowerCase()) > -1
      );
    case 'gte':
      return get(row, filter.columnId) >= filter.value;
    case 'lte':
      return get(row, filter.columnId) <= filter.value;
    case 'equals':
      return (get(row, filter.columnId)?.toString() || '') === filter.value;
    case 'startsWith':
      return get(row, filter.columnId).startsWith(filter.value);
    case 'in':
      return filter.value.toString().split(',').includes(get(row, filter.columnId)?.toString());
    case 'nin':
      return !filter.value.toString().split(',').includes(get(row, filter.columnId)?.toString());
    default:
      return true;
  }
};

const checkForString = (row: unknown, value) =>
  Object.values(row).some(rowValue =>
    rowValue?.toString().toLowerCase().includes(value.toLowerCase()),
  );

export function useTableFilter<T>(input: Array<T>, options?: UseTableFilterOptions) {
  const [searchBy, setSearchBy] = useState('');
  const [sortBy, setSortBy] = useState<Array<PaginationSort>>([]);
  const [filterBy, setFilterBy] = useState<Array<PaginationFilter>>([]);
  const numericColumnIds = useMemo(
    () =>
      new Set((options?.columns ?? []).filter(column => column.type === 'number').map(c => c.id)),
    [options?.columns],
  );

  const filteredItems = useMemo(() => {
    if (filterBy.length === 0 && searchBy === '' && sortBy.length === 0) {
      return input;
    }
    const filtered = input.filter(row => {
      const shouldFilterBy = filterBy.every(filter => checkForFilters(row, filter));
      const shouldSearchBy = checkForString(row, searchBy);
      return shouldFilterBy && shouldSearchBy;
    });

    if (sortBy.length === 0) {
      return filtered;
    }

    return orderBy(
      filtered,
      sortBy.map(sort => createSortIteratee(sort.columnId, numericColumnIds)),
      sortBy.map(sort => sort.order),
    );
  }, [filterBy, input, numericColumnIds, searchBy, sortBy]);

  return { setSearchBy, setSortBy, setFilterBy, filteredItems };
}
